import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Button,
  Pagination,
  Modal,
  Box,
  CircularProgress,
  Grid,
  Autocomplete,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  listMeasurements,
  deleteMeasurement,
  fetchSearchSuggestions,
} from "../../redux/actions/measurementActions";

const MeasurementList = ({
  title = "Measurement List",
  limit = 10,
  showPagination = true,
  showSearch = true,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [tag, setTag] = useState("");
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedMeasurementId, setSelectedMeasurementId] = useState(null);

  const { measurements, totalPages, loading } = useSelector(
    (state) => state.measurementList
  );

  const { success: deleteSuccess } = useSelector(
    (state) => state.measurementDelete
  );

  const { suggestions } = useSelector((state) => state.suggestionList);

  useEffect(() => {
    dispatch(listMeasurements(tag, page, limit));
  }, [dispatch, tag, page, limit, deleteSuccess]);

  useEffect(() => {
    dispatch(fetchSearchSuggestions());
  }, [dispatch]);

  const handleRowClick = (id) => {
    navigate(`/order/submit-quotation/${id}`);
  };

  const handleSearchInputChange = (event, newInputValue) => {
    setTag(newInputValue);
    setPage(1);
    dispatch(listMeasurements(newInputValue, 1, limit));
  };

  const handleSearch = (event, newValue) => {
    const selectedTag = newValue?.tag || "";
    setTag(selectedTag);
    setPage(1);
    dispatch(listMeasurements(selectedTag, 1, limit));
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleDeleteClick = (id) => {
    setSelectedMeasurementId(id);
    setShowModal(true);
  };

  const handleConfirmDelete = () => {
    dispatch(deleteMeasurement(selectedMeasurementId));
    setShowModal(false);
  };

  const handleCancelDelete = () => {
    setShowModal(false);
  };

  return (
    <>
      <TableContainer
        component={Paper}
        style={{
          marginTop: "1rem",
          padding: "1rem",
          paddingLeft: "2rem",
          paddingRight: "2rem",
        }}
      >
        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
        >
          <Grid item>
            <Typography
              variant="h6"
              component="div"
              style={{ marginBottom: "1rem" }}
            >
              {title}
            </Typography>
          </Grid>
          {showSearch && (
            <Grid item xs={12} sm={6} md={4}>
              <Autocomplete
                freeSolo
                options={suggestions}
                getOptionLabel={(option) => option.tag || ""}
                renderOption={(props, option) => (
                  <li
                    {...props}
                    style={{
                      padding: "10px",
                      borderBottom: "1px solid #ddd",
                      cursor: "pointer",
                    }}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography
                        variant="subtitle1"
                        component="div"
                        style={{ fontWeight: "bold" }}
                      >
                        {option.tag}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {option.clientName && <div>{option.clientName}</div>}
                        {option.email && <div>{option.email}</div>}
                        {option.address && <div>{option.address}</div>}
                      </Typography>
                    </Box>
                  </li>
                )}
                onInputChange={handleSearchInputChange}
                onChange={handleSearch}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search by Tag, client name or client email"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
          )}
        </Grid>

        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            style={{ height: "200px" }}
          >
            <CircularProgress color="secondary" />
          </Box>
        ) : measurements.length === 0 ? (
          <Typography>No measurements found.</Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Measurements Count</TableCell>
                <TableCell>Measurements Tag</TableCell>
                <TableCell>Total Sqft</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {measurements.map((measurement) => (
                <TableRow
                  key={measurement._id}
                  hover
                  onClick={() => handleRowClick(measurement._id)}
                  style={{ cursor: "pointer" }}
                >
                  <TableCell>
                    {new Date(measurement.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell>{measurement.measurements.length}</TableCell>
                  <TableCell>{measurement.tag}</TableCell>
                  <TableCell>{measurement.totalSqft}</TableCell>
                  <TableCell>
                    {" "}
                    <Chip
                      label={measurement.orderStatus}
                      color="info"
                    />
                  </TableCell>
                  <TableCell>
                    <i
                      className="bi bi-trash-fill text-danger"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(measurement._id);
                      }}
                    ></i>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {showPagination && (
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            style={{ marginTop: "1rem" }}
          />
        )}
      </TableContainer>

      {/* Confirmation Modal */}
      <Modal
        open={showModal}
        onClose={handleCancelDelete}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 300,
            bgcolor: "background.paper",
            border: "2px solid #000",
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography id="modal-title" variant="h6" component="h2">
            Confirm Delete
          </Typography>
          <Typography id="modal-description" sx={{ mt: 2 }}>
            Are you sure you want to delete this measurement?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 2,
            }}
          >
            <Button onClick={handleCancelDelete} sx={{ marginRight: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default MeasurementList;
